import { history } from "components/common/BrowserRouter";
import { EnumMasterInsuranceSubState } from "constants/enums/master-insurance-sub-state";
import { last } from "lodash";
import qs from "qs";

// Helper to check if a string is a valid UUID
export const isValidUUID = (str: string | null): boolean => {
  if (!str) return false;
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(str);
};

// Helper to get current tenant ID from URL
export const getTenantIdFromUrl = (): string | null => {
  const pathname = window.location.pathname;
  const match = pathname.match(/^\/([^/]+)\//);
  const tenantId = match ? match[1] : null;

  // Exclude auth paths and other non-tenant paths
  if (
    tenantId &&
    (tenantId === "404" || tenantId === "500" || tenantId === "auth")
  ) {
    return null;
  }

  return tenantId;
};

// prettier-ignore
export class Path extends String {
  newTab = () => {
    window.open(this.toString(), "__blank");
  };

  push = (extendedPath: String = "") => {
    if (history) {
      history.push(`${this.toString()}${extendedPath ?? ""}`);
    }
  };

  navigateReplace = (extendedPath: String = "") => {
    if (history) {
      history.replace(`${this.toString()}${extendedPath ?? ""}`);
    }
  };

  reload = () => {
    window.location.href = this.toString();
  };

  makePath = (
    path: Function,
    options: { withTenant?: boolean } = {}
  ): Function => {
    const { withTenant = true } = options;
    return (...args: any[]) => {
      let pathMethod = typeof path === "string" ? () => path : path;
      let absolutePath = null;

      if (last(args) instanceof Object) {
        const params = args.slice(0, args.length - 1);

        const query = last(args);
        const queryString = qs.stringify(query);

        absolutePath = `${this}/${pathMethod(...params)}?${queryString}`;
      } else {
        absolutePath = `${this}/${pathMethod(...args)}`;
      }

      // Add tenant_id prefix for main paths
      if (withTenant) {
        const tenantId = getTenantIdFromUrl();
        if (tenantId) {
          absolutePath = `/${tenantId}${absolutePath}`;
        }
      }

      return new Path(absolutePath.replace(/\/+/g, "/"));
    };
  };

  page404Path = this.makePath(() => `404`, { withTenant: false });
  page500Path = this.makePath(() => `500`, { withTenant: false });

  signInPath = this.makePath(() => `auth/sign-in`, { withTenant: false });
  signUpPath = this.makePath(() => `auth/sign-up`, { withTenant: false });
  setupPasswordPath = this.makePath(() => `auth/setup-password`, {
    withTenant: false,
  });
  resetPasswordPath = this.makePath(() => `auth/reset-password`, {
    withTenant: false,
  });

  adminsPath = this.makePath(() => `admins`);
  adminNewPath = this.makePath(() => `admins/new`);
  adminEditPath = this.makePath((id: any) => `admins/${id}/edit`);

  rolesPath = this.makePath(() => `roles`);
  roleNewPath = this.makePath(() => `roles/new`);
  roleEditPath = this.makePath((id: any) => `roles/${id}/edit`);

  insurancePackageNewPath = this.makePath(
    (insuranceId: any) => `insurance/${insuranceId}/packages/new`
  );
  insurancePackageEditPath = this.makePath(
    (insuranceId: any, id: any) =>
      `insurance/${insuranceId}/packages/${id}/edit`
  );
  insurancePackageDuplicatePath = this.makePath(
    (insuranceId: any, duplicateId: any) =>
      `insurance/${insuranceId}/packages/duplicate/${duplicateId}`
  );
  insurancePackageViewInsurancePath = this.makePath(
    (insuranceId: any, id: any) =>
      `insurance/${insuranceId}/packages/insurance/${id}`
  );

  insurancePath = this.makePath(() => `insurance`);
  insuranceDetailPath = this.makePath((id: any) => `insurance/${id}`);
  addInsurancePath = this.makePath(
    (id: any) => `insurance/${id}/insurance_creating/new`
  );
  editInsurancePath = this.makePath(
    (id: any, insuranceId: any) =>
      `insurance/${id}/insurance_creating/edit/${insuranceId}`
  );
  duplicateInsurancePath = this.makePath(
    (id: any, duplicateId: any) =>
      `insurance/${id}/insurance_creating/duplicate/${duplicateId}`
  );

  enrollmentPath = this.makePath(() => `enrollment`);
  enrollmentDetailPath = this.makePath((id: any) => `enrollment/${id}`);
  enrollmentInsurancePath = this.makePath(
    (id: any, insuranceId: any) =>
      `enrollment/${id}/insurance_plans/${insuranceId}`
  );

  // reportPath = this.makePath(() => `report`);
  marketplaceReportPath = this.makePath(() => `marketplace_report`);

  // settingsPath = this.makePath(() => `settings`);

  insuranceCompanyPath = this.makePath(() => `insurance_company`);
  insuranceCompanyNewPath = this.makePath(() => `insurance_company/new`);
  insuranceCompanyEditPath = this.makePath(
    (id: any) => `insurance_company/${id}/edit`
  );
  insuranceCompanyNewQuestionPath = this.makePath(
    (id: any) => `insurance_company/${id}/question/new`
  );
  insuranceCompanyEditQuestionPath = this.makePath(
    (id: any, questionId: any) =>
      `insurance_company/${id}/question/${questionId}/edit`
  );

  dynamicConfigPath = this.makePath(() => `dynamic_config`);
  occupationPath = this.makePath(() => `occupation`);
  occupationNewPath = this.makePath(() => `occupation/new`);
  occupationEditPath = this.makePath((id: any) => `occupation/${id}/edit`);

  notificationManagementPath = this.makePath(() => `notification_management`);
  notificationManagementNewPath = this.makePath(
    () => `notification_management/new`
  );
  notificationManagementEditPath = this.makePath(
    (id: any) => `notification_management/${id}/edit`
  );
  notificationManagementHistoryPath = this.makePath(
    () => `notification_management/history`
  );
  workspacePath = this.makePath(() => `workspace`);
  workspaceNewPath = this.makePath(() => `workspace/new`);
  workspaceEditPath = this.makePath((id: any) => `workspace/${id}/edit`);

  masterInsurancePlanDetailPath = this.makePath(
    (id: any) => `master_insurance_plan/${id}`
  );
  masterInsurancePlansPath = this.makePath(() => `master_insurance_plan`);

  paymentTransactionPath = this.makePath(() => `payment_transaction`);
  paymentTransactionDetailPath = this.makePath(
    (id: any) => `payment_transaction/${id}`
  );
  insuranceTransactionPath = this.makePath(() => `insurance_transaction`);
  insuranceTransactionDetailPath = this.makePath(
    (id: string) => `insurance_transaction/${id}`
  );
  insuranceTransactionInstallmentDetailPath = this.makePath(
    (id: string, installmentId: string) => `insurance_transaction/${id}/installment/${installmentId}`
  );

  overdueTransactionPath = this.makePath(() => `overdue_transaction`);

  homePath = this.adminsPath;

  leadPath = this.makePath(() => `lead`);

  userPath = this.makePath(() => `user`);
  userDetailPath = this.makePath((id: string) => `user/${id}`);

  riderConfigPath = this.makePath(() => `rider_config`);
  frontStoreManagementPath = this.makePath(() => `front_store_management`);
  frontStoreManagementDetailPath = this.makePath(
    (id: string) => `front_store_management/${id}`
  );
  frontStoreAddFatcaQuestionPath = this.makePath(
    (id: string) => `front_store_management/${id}/fatca-question/add`
  );
  frontStoreEditFatcaQuestionPath = this.makePath(
    (id: string, questionId: string) =>
      `front_store_management/${id}/fatca-question/${questionId}/edit`
  );

  nameChangeRequestPath = this.makePath(() => `name_change_request`);

  insuranceValidationDetail = this.makePath(
    (insuranceId: string, id: string) =>
      `insurance/${insuranceId}/${EnumMasterInsuranceSubState.validating}/${id}`
  );

  brokerPath = this.makePath(() => `broker`);
  brokerNewPath = this.makePath(() => `broker/new`);
  brokerEditPath = this.makePath((id: any) => `broker/${id}/edit`);

  orderPath = this.makePath(() => `order`);
  orderDetailPath = this.makePath((id: any) => `order/${id}`);
}

export default new Path();
